import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type AppendMessage,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import {
  useConversationStream,
  type UseConversationStreamReturn,
} from '../../../view-system/hooks/useConversationStream';
import type {
  StreamMessage,
} from '../../../types/stream-protocol';
import {
  TransparencyStepsProvider,
  TransparencyTurnsProvider,
} from '../../shared/TransparencyStepsContext';
import { deriveTransparencyTurns } from './deriveTransparencyTurns';

// convertMessage: StreamMessage -> ThreadMessageLike

function convertMessage(msg: StreamMessage): ThreadMessageLike {
  switch (msg.type) {
    case 'user':
      return {
        role: 'user',
        content: [{ type: 'text', text: (msg).content }],
        id: msg.messageId,
        createdAt: new Date(msg.timestamp),
      };

    case 'assistant': {
      const aMsg = msg;
      const parts: Array<
        | { type: 'reasoning'; text: string }
        | { type: 'text'; text: string }
      > = [];

      // Add reasoning parts from transparency steps (before text content)
      // Steps data is passed via TransparencyStepsContext (built in the component)
      // instead of as `data` content parts to avoid the dataRenderers scope crash.
      if (aMsg.stats?.steps) {
        const visibleSteps = aMsg.stats.steps.filter((s) => s.tier <= 2);
        for (const step of visibleSteps) {
          parts.push({
            type: 'reasoning',
            text: step.narrativeAction ?? step.name,
          });
        }
      }

      // Add text content
      if (aMsg.content) {
        parts.push({ type: 'text', text: aMsg.content });
      }

      // Ensure at least one part exists
      if (parts.length === 0) {
        parts.push({ type: 'text', text: '' });
      }

      return {
        role: 'assistant',
        content: parts,
        id: msg.messageId,
        createdAt: new Date(msg.timestamp),
        // Messages without stats are still streaming (synthetic from delta accumulation)
        ...(aMsg.stats ? {} : { status: { type: 'running' as const } }),
      };
    }

    case 'assistant_delta':
      // Should not reach here — deltas are accumulated into synthetic assistant messages
      return {
        role: 'system',
        content: [{ type: 'text', text: '' }],
        id: msg.messageId,
      };

    case 'status':
      return {
        role: 'assistant',
        content: [{ type: 'text', text: (msg).message }],
        id: msg.messageId,
        createdAt: new Date(msg.timestamp),
        status: { type: 'running' },
        // Additive-only tag (V1 never reads `metadata.custom` — see
        // `ThreadMessageLike`'s own type, coding-standards.md "additive-
        // only" precedent) so `ChatThreadV2` (bead terra-design-v2-chat-yub.5)
        // can tell a transient status-ticker row apart from the synthetic
        // streaming assistant row and skip rendering it as a message bubble
        // — both currently produce an identical `role: 'assistant'`,
        // `status: { type: 'running' }` shape otherwise. Zero behavior
        // change for V1's `Thread.tsx`/`MarkdownText.tsx`, which don't read it.
        metadata: { custom: { streamType: 'status' } },
      };

    case 'error':
      return {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: `Error: ${(msg).message}`,
          },
        ],
        id: msg.messageId,
        createdAt: new Date(msg.timestamp),
        status: { type: 'incomplete', reason: 'error' },
      };

    case 'metadata':
    case 'turn_superseded':
    case 'turn_done':
      // All of `metadata`, `turn_superseded` and `turn_done` land here, from
      // both sides of this merge. `turn_superseded`/`turn_done` come from
      // master: `useConversationStream` consumes them and never appends them,
      // so they are not rendered. `metadata` is filtered out of the array
      // `DurableStreamRuntimeProvider` hands to `useExternalStoreRuntime`
      // (Behavior Spec #13) — derivation input only, never a rendered row.
      // All three are kept as a safety net for any OTHER `convertMessage`
      // caller that still passes the full `messages` array (there are none
      // today).
      return {
        role: 'system',
        content: [{ type: 'text', text: '' }],
        id: msg.messageId,
      };

    case 'steps_update':
      // Same filtering as `metadata` above — `steps_update` frames feed
      // `deriveTransparencyTurns` and never reach this function in the real
      // runtime. Safety-net fallback only (Behavior Spec #13).
      return {
        role: 'system',
        content: [{ type: 'text', text: '' }],
        id: msg.messageId,
      };
  }
}

// Props

export interface DurableStreamRuntimeProviderProps {
  conversationId: string;
  children: ReactNode;
  /**
   * Called when the conversation is detected as deleted (404).
   *
   * INERT when `externalStream` is supplied: the internal
   * `useConversationStream` call is then given a null id (a documented
   * no-op — see `useConversationStream.ts`'s early return), so it never
   * fires this callback. In external mode the deletion signal instead
   * arrives as `externalStream.error.kind === 'deleted'`, and the deleted
   * banner below renders from that injected error — the external stream's
   * owner (the Flow multi-session host, U3) is responsible for its own
   * deletion handling.
   */
  onConversationDeleted?: () => void;
  /**
   * A message queued by the parent's lazy-create flow (U8). When present, the
   * provider flushes it through `send` exactly once after mount, then calls
   * `onPendingConsumed`. This is how the FIRST message of a freshly-created
   * conversation reaches the stream: the parent creates the row, commits the
   * id (remounting this provider with the new conversationId), and hands the
   * verbatim text down here.
   */
  pendingFirstMessage?: string | null;
  /** Called after `pendingFirstMessage` has been flushed (clears the queue). */
  onPendingConsumed?: () => void;
  /**
   * An externally-owned stream to consume instead of opening a second
   * `EventSource` for the same conversation. When provided, the internal
   * `useConversationStream` hook is called with a null conversation id (its
   * documented no-op path), so exactly one EventSource exists for the
   * conversation — the one owned by whoever produced `externalStream`. All
   * downstream reads (runtime messages/isRunning, the `KeystoneChatStream`
   * context, the error banners, the transparency steps map) come from this
   * stream instead of the internal hook's own state.
   */
  externalStream?: UseConversationStreamReturn;
}

// Keystone chat stream context — exposes live stream state to the Composer
// (rendered as a child inside this provider, mirroring how Edit-with-AI's
// ChatPanel reads useSkillStreamContext). `messageCount` drives the persona
// lock; `isRunning` disables the input mid-stream; `send` is the live ask.

export interface KeystoneChatStreamValue {
  messageCount: number;
  isRunning: boolean;
  send: (question: string) => Promise<void>;
}

const KeystoneChatStreamContext = createContext<KeystoneChatStreamValue | null>(
  null,
);

export function useKeystoneChatStream(): KeystoneChatStreamValue {
  const ctx = useContext(KeystoneChatStreamContext);
  if (!ctx) {
    throw new Error(
      'useKeystoneChatStream must be used within a DurableStreamRuntimeProvider',
    );
  }
  return ctx;
}

// Component

export function DurableStreamRuntimeProvider({
  conversationId,
  children,
  onConversationDeleted,
  pendingFirstMessage,
  onPendingConsumed,
  externalStream,
}: DurableStreamRuntimeProviderProps) {
  // The hook call stays unconditional (never `if (externalStream) … else
  // useConversationStream(…)`) — `useConversationStream` no-ops on a null id
  // (`useConversationStream.ts` line 61), which is exactly what lets an
  // external stream own the one real EventSource for this conversation.
  const internal = useConversationStream(
    externalStream ? null : conversationId,
    { onConversationDeleted },
  );
  const { messages, isRunning, error, send } = externalStream ?? internal;

  // ---- Flush the lazily-created conversation's first message (U8) ----------
  // The parent created the row, committed the id (remounting us), and handed
  // down the verbatim text. Send it exactly once. Guard with a ref so a
  // re-render (e.g. isRunning toggling) never double-sends. `send` itself is
  // a no-op until conversationId is set, which it always is here.
  const flushedRef = useRef(false);
  const onPendingConsumedRef = useRef(onPendingConsumed);
  onPendingConsumedRef.current = onPendingConsumed;
  useEffect(() => {
    if (!pendingFirstMessage || flushedRef.current) return;
    flushedRef.current = true;
    void send(pendingFirstMessage);
    onPendingConsumedRef.current?.();
  }, [pendingFirstMessage, send]);

  const streamValue = useMemo<KeystoneChatStreamValue>(
    () => ({ messageCount: messages.length, isRunning, send }),
    [messages.length, isRunning, send],
  );

  // Derive both the legacy `stepsMap` (TransparencyStepsContext) and the
  // new turn-keyed `turnsMap` (TransparencyTurnsContext) from the SAME
  // `messages` array in one pass — the ONE derivation (bead
  // terra-design-v2-chat-yub.8), not a second copy in ChatRunSteps or
  // anywhere in shell-next/chat/.
  const { stepsMap, turnsMap } = useMemo(
    () => deriveTransparencyTurns(messages),
    [messages],
  );

  // Runtime rows exclude `steps_update`/`metadata` — these are derivation
  // input only (consumed above), not renderable rows (Behavior Spec #13).
  // Everything else (user/status/assistant/assistant_delta-synthesized/
  // error) is unchanged and in the original order.
  const runtimeMessages = useMemo(
    () => messages.filter((m) => m.type !== 'steps_update' && m.type !== 'metadata'),
    [messages],
  );

  // ---- Optimistic onNew handler ----
  const onNew = useCallback(
    async (appendMessage: AppendMessage) => {
      const text = appendMessage.content
        .filter(
          (part): part is { type: 'text'; text: string } =>
            part.type === 'text',
        )
        .map((part) => part.text)
        .join('\n');

      if (!text.trim()) return;
      try {
        await send(text);
      } catch {
        // The internal hook's own `send` never rejects (it swallows POST
        // failures and lets the SSE stream deliver the error instead). An
        // injected `externalStream.send` has no such guarantee, and
        // `@assistant-ui/react`'s ExternalThread invokes `onNew`
        // fire-and-forget — no await, no catch at the call site — so an
        // unguarded rejection here would surface as an unhandled promise
        // rejection. Swallow it; the stream's own error state (if any) is
        // what should drive the UI.
      }
    },
    [send],
  );

  // ---- Build the external store runtime ----
  const runtime = useExternalStoreRuntime({
    isRunning,
    messages: runtimeMessages,
    convertMessage,
    onNew,
  });

  // ---- Error banners ----
  if (error?.kind === 'deleted') {
    return (
      <div className="flex items-center justify-center h-full text-icon-subtle">
        <p>This conversation has been deleted.</p>
      </div>
    );
  }

  if (error?.kind === 'session-expired') {
    return (
      <div className="flex items-center justify-center h-full text-amber-600">
        <p>Session expired. Please refresh the page to continue.</p>
      </div>
    );
  }

  if (error?.kind === 'unavailable') {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        <p>Chat temporarily unavailable. Please try again later.</p>
      </div>
    );
  }

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <TransparencyStepsProvider value={stepsMap}>
        <TransparencyTurnsProvider value={turnsMap}>
          <KeystoneChatStreamContext.Provider value={streamValue}>
            {children}
          </KeystoneChatStreamContext.Provider>
        </TransparencyTurnsProvider>
      </TransparencyStepsProvider>
    </AssistantRuntimeProvider>
  );
}
